import { lazy} from 'react';

import {
	createRootRoute,
	createRouter,
	createRoute,
	AnyRoute,
	Outlet,
} from '@tanstack/react-router';

import { basepath } from '@/constants';
import config from '@/config';
import { NotFound } from '@/views/RootRoute';
import { issueSearchSchema } from '@/views/issue/Issue.hooks';
import { SessionTracker } from '@/components/session/SessionTracker';
import { DevTools } from '@/components/DevTools';

// Lazy loaded routes
const LazyHome = lazy(() => import('./home/Home'));
const LazyPreview = lazy(() => import('@/templates/daily/preview/Preview'));
const LazyDrafts = lazy(() => import('@/views/drafts/Drafts'));
const LazyIssue = lazy(() => import('./issue/Issue'));
const LazyCallback = lazy(() => import('@/templates/basic/callback/Callback'));
const LazyLogout = lazy(() => import('@/templates/basic/auth/Logout'));
const LazySubscriptions = lazy(
	() => import('@/templates/basic/subscriptions/Subscriptions'),
);
const LazySuccess = lazy(
	() => import('@/templates/basic/subscriptions/success/Success'),
);

const LazySection = lazy(() => import('./section/Section'));
const LazyGames = lazy(() => import('./games/Games'));
import { getRoute as getArticleRoute } from '@views/article/Article.route';
import { TrackingView } from './tracker/Tracker';

const rootRoute = createRootRoute({
	component: RootRouteComponent,
});

/* Routes declaration */

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/',
	component: LazyHome,
});

const sectionRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazySection,
	path: 'sections/$id',
});

const gamesRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyGames,
	path: 'games',
});

const issueRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyIssue,
	validateSearch: issueSearchSchema,
	path: 'issues/$id',
});

const issueArticleRoute = createRoute({
	getParentRoute: () => issueRoute,
	validateSearch: issueSearchSchema,
	component: LazyIssue,
	path: '$articleId',
});

const previewRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyPreview,
	path: 'preview/$id',
});

const subscriptionsRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazySubscriptions,
	path: 'subscriptions',
});

const subscriptionsSuccessRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazySuccess,
	path: 'subscriptions/success',
});

const callbackRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyCallback,
	path: 'callback',
});

const logoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyLogout,
	path: 'logout',
});

const draftsRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: LazyDrafts,
	path: 'drafts',
});

const trackerRoute = createRoute({
	getParentRoute: () => rootRoute,
	component: TrackingView,
	path: 'track',
});

const articleRoute = getArticleRoute({
	parentRoute: rootRoute,
});

/* END of routes declaration */

const routes: Array<AnyRoute> = [
	indexRoute,
	sectionRoute,
	gamesRoute,
	issueRoute,
	issueArticleRoute,
	previewRoute,
	subscriptionsRoute,
	subscriptionsSuccessRoute,
	callbackRoute,
	logoutRoute,
	trackerRoute,
	articleRoute,
];

if (config?.app?.admin) {
	routes.push(draftsRoute);
}

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({
	routeTree,
	basepath,
	defaultNotFoundComponent: NotFound,
});

function RootRouteComponent() {
	// useNavigationHistory();
	return (
		<>
			<SessionTracker />
			<Outlet />

			<DevTools router={router} />
		</>
	);
}
